/**
 * ResourceDetails Component
 * 
 */

import { FunctionComponent, useEffect } from 'react'
import {
  Box,
  Skeleton,
  Typography,
  Link,
  Button,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import DashboardLayout from '../../components/layout/layout'
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useGetResourcesByItem } from '../../hooks/api/user';
import { handleFileClick } from '../../utils/fileHandler';

/**
 * ResourceDetails Component
 * 
 * This component displays a list of resources (files) for a specific category/itemId.
 * It's used for pages like PriceSheets, Obsolete Products, etc.
 * 
 * Key Features:
 * - Fetches resources using the resources-by-item API endpoint
 * - Displays files with clickable links for download/viewing
 * - Handles both PDF files (inline viewing) and other files (download)
 * - Provides navigation back to Sales Tools main page
 */
const ResourceDetails: FunctionComponent = () => {
  const theme = useTheme();
  const { itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Mapping of itemId to category names for display purposes
  // This allows direct navigation to specific resource categories
  const categoryMapping: { [key: string]: string } = {
    '15': 'Price Sheets',           // PriceSheets sidebar menu item
    '21': 'Obsolete Products',      // Obsolete Cross Reference sidebar menu item
    '62': 'Competitor Cross References', // Competitor Cross Reference sidebar menu item
    '56': 'Product Highlights',     // Product Highlights page
    '65': 'S5 Product Training'     // S5 Product Training page
  };
  
  // Mapping of itemId to activeMenu for proper sidebar highlighting
  // This ensures the correct sidebar menu item is highlighted when viewing resources
  const activeMenuMapping: { [key: string]: string } = {
    '15': 'priceSheet',             // Highlights "Price Sheet" in sidebar
    '21': 'obsoleteCrossReference', // Highlights "Obsolete Cross Reference" in sidebar
    '62': 'competitorCrossReference', // Highlights "Competitor Cross Reference" in sidebar
    '56': 'resources',              // Highlights "Sales Tools" in sidebar
    '65': 'resources'               // Highlights "Sales Tools" in sidebar
  };
  
  // Get category name from navigation state or mapping, fallback to 'Category'
  const categoryName = location.state?.categoryName || categoryMapping[itemId || ''] || 'Category';
  // Get active menu for sidebar highlighting
  const activeMenu = activeMenuMapping[itemId || ''] || 'resources';

  // Redirect to resources main page if no itemId is provided
  useEffect(() => {
    if (!itemId) {
      navigate('/resources');
    }
  }, [itemId, navigate]);

  // Fetch resources for the specific itemId using the resources-by-item API
  // This is more efficient than searching by category name
  const { data: resources, isLoading } = useGetResourcesByItem(itemId);

  return (
    <DashboardLayout title={"Sales Tools"} initialBreadcrumbs={"Sales Tools"} levelBreadcrumbs={""} breadcrumbs={'Sales Tools'} activeMenu={activeMenu}>
      <Box>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Sales Tools: {categoryName}
        </Typography>

        {/* Loading state - show skeleton placeholders while fetching resources */}
        {isLoading ? (
          <>
            <Skeleton width="38%" />
            <Skeleton width="100%" />
            <Skeleton width="100%" />
            <Skeleton width="61%" />
          </>
        ) : (
          /* Resources list - display files if available, otherwise show no documents message */
          resources && Array.isArray(resources) && resources.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {resources.map((resource, index) => (
                <Box
                  key={resource.itemId || index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 0',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      borderRadius: 1,
                    },
                  }}
                  onClick={() => {
                    // Handle file click - construct absolute URL to prevent redirect issues
                    if (resource.filename) {
                      // Get the download URL from API response or construct it
                      const fileUrl = resource.downloadUrl || `/api/v1/resources/download/${itemId}/${encodeURIComponent(resource.filename)}`;
                      
                      // This prevents the browser from trying to resolve relative URLs against the frontend server (port 3000)
                      // Instead, it ensures the URL points to the backend server (port 8080)
                      const absoluteUrl = fileUrl.startsWith('http') 
                        ? fileUrl 
                        : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${fileUrl}`;
                      
                      // Use the file handler utility which determines whether to open inline or download
                      // PDFs will open in new tab, Excel files will download
                      handleFileClick(absoluteUrl, resource.filename);
                    } else if (resource.link) {
                      // Handle external links (if any) - open in new tab
                      window.open(resource.link, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  <Typography
                    sx={{
                      textDecoration: 'none',
                      color: '#1976d2',
                      fontWeight: 400,
                      ...theme.typography.body1,
                      textAlign: 'left',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#1565c0',
                      },
                    }}
                  >
                    {resource.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            /* No resources available - show message */
            <Typography variant="body2" sx={{ color: 'gray' }}>
              No documents available for this category.
            </Typography>
          )
        )}

        {/* Navigation button - allows users to return to Sales Tools main page */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>
      </Box> 
    </DashboardLayout>
  )
}

export default ResourceDetails